import { isNumber } from 'lodash';
import React, { useState } from 'react';
import { InputNumber } from '@meitu/saas-ui';
import { type IVectorBorderRadiusProps } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { useLocaleStore } from '@/i18n';
import './style.less';

type Props = {
    borderRadius: IVectorBorderRadiusProps;
    maxValue: number;
    onClose: () => void;
    onChange: (corner: keyof IVectorBorderRadiusProps, value: number) => void;
    onAfterChange: (corner: keyof IVectorBorderRadiusProps, value: number) => void;
};

export const BorderRadiusInput: React.FC<Props> = ({
    borderRadius: _borderRadius,
    maxValue,
    onClose,
    onChange,
    onAfterChange,
}) => {
    const { $t } = useLocaleStore();

    // 由于 BorderRadiusInput 嵌入在动态弹出层中，图层的 borderRadius 变化并不会渲染弹出层
    // 所以这里需要用本地状态来维护
    const [borderRadius, setBorderRadius] = useState(_borderRadius);

    const handleChange = (corner: keyof IVectorBorderRadiusProps, value: number | null) => {
        if (!isNumber(value)) return;

        setBorderRadius((prev) => ({ ...prev, [corner]: value }));
        onChange(corner, value);
    };

    const handleAfterChange = (corner: keyof IVectorBorderRadiusProps, value: number | null) => {
        if (!isNumber(value)) return;

        setBorderRadius((prev) => ({ ...prev, [corner]: value }));
        onAfterChange(corner, value);
    };

    return (
        <div className="border-radius-input" data-operate-type="canDrag">
            <div className="border-radius-input__header" data-operate-type="canDrag">
                <div className="border-radius-input__title" data-operate-type="canDrag">
                    <IconFont name="AmplificationBold" fontSize={14} color="var(--content-btn-transparency)" />
                    <span>{$t((v) => v.panel.vectorPanel.modalTitle)}</span>
                </div>
                <div className="border-radius-input__close" onClick={onClose}>
                    <IconFont name="CrossBlack" fontSize={12} color="var(--content-system-secondary)" />
                </div>
            </div>

            <div className="border-radius-input__content">
                <div className="border-radius-input__item">
                    <IconFont name="UpperLeftBold" fontSize={24} color="var(--content-system-tertiary)" />
                    <InputNumber
                        min={0}
                        max={maxValue}
                        value={borderRadius.leftTop}
                        onChange={(value) => handleChange('leftTop', value)}
                        onChangeAfter={(value) => handleAfterChange('leftTop', value)}
                    />
                </div>

                <div className="border-radius-input__item">
                    <IconFont name="UpperRight" fontSize={24} color="var(--content-system-tertiary)" />
                    <InputNumber
                        min={0}
                        max={maxValue}
                        value={borderRadius.rightTop}
                        onChange={(value) => handleChange('rightTop', value)}
                        onChangeAfter={(value) => handleAfterChange('rightTop', value)}
                    />
                </div>

                <div className="border-radius-input__item">
                    <IconFont name="LeftLowerBold" fontSize={24} color="var(--content-system-tertiary)" />
                    <InputNumber
                        min={0}
                        max={maxValue}
                        value={borderRadius.leftBottom}
                        onChange={(value) => handleChange('leftBottom', value)}
                        onChangeAfter={(value) => handleAfterChange('leftBottom', value)}
                    />
                </div>

                <div className="border-radius-input__item">
                    <IconFont name="LowRightBold" fontSize={24} color="var(--content-system-tertiary)" />
                    <InputNumber
                        min={0}
                        max={maxValue}
                        value={borderRadius.rightBottom}
                        onChange={(value) => handleChange('rightBottom', value)}
                        onChangeAfter={(value) => handleAfterChange('rightBottom', value)}
                    />
                </div>
            </div>
        </div>
    );
};
